import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DailyIframe from '@daily-co/daily-js';
import { Video, MapPin, Phone, ShieldCheck, Loader2, CalendarDays, Clock, HeartHandshake, CheckCircle2, LogIn, LogOut } from 'lucide-react';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Senior-friendly, NO-LOGIN "Visit Pass". A family member books on behalf of their loved
 * one and shares this link; the senior just opens it and taps one big button — no app,
 * no password. Designed for low vision & low tech-confidence: extra-large type, high
 * contrast, generous spacing, big tap targets, plain language, and an always-visible
 * "Prefer to call?" line.
 */
export default function VisitPass() {
  const { token } = useParams();
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [checkInBusy, setCheckInBusy] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);
  const [status, setStatus] = useState({ arrived_at: null, completed_at: null, status: null });
  const frameRef = useRef(null);
  const callFrameRef = useRef(null);

  const destroyFrame = useCallback(() => {
    if (callFrameRef.current) {
      try { callFrameRef.current.destroy(); } catch (e) { /* noop */ }
      callFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/visit-pass/${token}`);
        if (!cancelled) setPass(res.data);
        // also load tracking status (arrived/completed)
        const st = await axios.get(`${API}/public/visit-pass/${token}/status`);
        if (!cancelled) setStatus(st.data || {});
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "We couldn't find that visit.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; destroyFrame(); };
  }, [token, destroyFrame]);

  const checkIn = async () => {
    setCheckInBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/visit-pass/${token}/check-in`, {});
      setStatus({ arrived_at: data.arrived_at, completed_at: null, status: data.status });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Check-in failed');
    } finally { setCheckInBusy(false); }
  };

  const completeVisit = async () => {
    setCompleteBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/visit-pass/${token}/complete`, {});
      setStatus({ arrived_at: status.arrived_at, completed_at: data.completed_at, status: data.status });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Complete failed');
    } finally { setCompleteBusy(false); }
  };

  const joinVideo = async () => {
    setJoining(true);
    try {
      const { data: room } = await axios.get(`${API}/public/visit-pass/${token}/room`);
      if (!room.daily_enabled || !room.room_url || !room.token) {
        setError('Live video isn’t ready for this visit yet. Please use the phone number below for help.');
        setJoining(false);
        return;
      }
      setCallActive(true);
      // allow the container to render before mounting the iframe
      setTimeout(async () => {
        try {
          const frame = DailyIframe.createFrame(frameRef.current, {
            showLeaveButton: true,
            iframeStyle: { width: '100%', height: '100%', border: '0' },
          });
          callFrameRef.current = frame;
          frame.on('left-meeting', () => { destroyFrame(); setCallActive(false); setJoining(false); });
          frame.on('error', () => { setError('There was a problem connecting. Please call us using the number below.'); });
          // Daily's prejoin screen lets the senior test their camera & sound first.
          await frame.join({ url: room.room_url, token: room.token });
          setJoining(false);
        } catch (e) {
          setError('We couldn’t start the video. Please call us using the number below.');
          setCallActive(false);
          setJoining(false);
          destroyFrame();
        }
      }, 50);
    } catch (e) {
      setError('We couldn’t open the video visit. Please call us using the number below.');
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bone-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-cn-primary animate-spin" />
      </div>
    );
  }

  if (error && !callActive) {
    return (
      <div className="min-h-screen bg-cn-bone-100 flex flex-col items-center justify-center px-6 text-center" data-testid="visit-pass-error">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-5">
          <Phone className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 font-display">We're here to help</h1>
        <p className="text-xl text-slate-600 mt-3 max-w-md">{error}</p>
        {pass?.support_phone && (
          <a href={`tel:${pass.support_phone}`} className="mt-8 inline-flex items-center gap-3 bg-cn-primary text-white text-2xl font-semibold px-8 py-5 rounded-2xl" data-testid="visit-pass-call-btn">
            <Phone className="w-7 h-7" /> Call {pass.support_phone}
          </a>
        )}
      </div>
    );
  }

  // Active video call — full screen
  if (callActive) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="visit-pass-call">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <div className="flex items-center gap-2 text-white">
            <ShieldCheck className="w-5 h-5 text-cn-sage-300" />
            <span className="text-lg font-semibold">Your video visit</span>
          </div>
          {pass?.support_phone && (
            <a href={`tel:${pass.support_phone}`} className="text-cn-sage-200 text-base inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> Need help?
            </a>
          )}
        </div>
        <div ref={frameRef} className="flex-1 w-full" data-testid="visit-pass-frame" />
        {joining && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/70">
            <div className="text-center text-white">
              <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-cn-sage-300" />
              <p className="text-xl">Connecting you now…</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  const isTele = pass?.is_telehealth;

  const friendlyDate = (d) => {
    if (!d) return '';
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  };
  const friendlyTime = (t) => {
    if (!t) return '';
    const [h, m] = String(t).split(':');
    const hr = parseInt(h, 10);
    if (isNaN(hr)) return t;
    const ampm = hr >= 12 ? 'PM' : 'AM';
    const h12 = hr % 12 || 12;
    return `${h12}:${(m || '00').padStart(2, '0')} ${ampm}`;
  };

  return (
    <div className="min-h-screen bg-cn-bone-100 text-slate-900" data-testid="visit-pass-page">
      {/* Simple header */}
      <header className="py-5 px-6 flex justify-center border-b border-cn-border bg-white">
        <CaraLoomLogo size={32} />
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 sm:py-14">
        {/* Greeting */}
        <p className="text-2xl text-slate-600" data-testid="visit-pass-greeting">
          Hello{pass?.recipient_name ? `, ${pass.recipient_name}` : ''} 👋
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold font-display mt-2 leading-tight">
          You have a {pass?.visit_type_label?.toLowerCase()}.
        </h1>

        {/* Clinician card */}
        <div className="mt-8 bg-white rounded-3xl border border-cn-border p-6 sm:p-8 flex items-center gap-5" data-testid="visit-pass-clinician">
          {pass?.clinician_photo ? (
            <img src={pass.clinician_photo} alt={pass.clinician_name}
                 className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover flex-shrink-0" />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-cn-sage-100 flex items-center justify-center flex-shrink-0">
              <HeartHandshake className="w-10 h-10 text-cn-primary" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-base text-slate-500">Your clinician</p>
            <p className="text-2xl sm:text-3xl font-bold font-display truncate">{pass?.clinician_name}</p>
            {pass?.clinician_license && (
              <span className="inline-flex items-center gap-1.5 mt-1 text-base text-cn-primary font-medium">
                <ShieldCheck className="w-4 h-4" /> {pass.clinician_license} · Verified
              </span>
            )}
          </div>
        </div>

        {/* When */}
        <div className="mt-5 bg-white rounded-3xl border border-cn-border p-6 sm:p-8" data-testid="visit-pass-when">
          <div className="flex items-center gap-3 text-2xl sm:text-3xl font-semibold">
            <CalendarDays className="w-7 h-7 text-cn-primary flex-shrink-0" />
            <span>{friendlyDate(pass?.date)}</span>
          </div>
          <div className="flex items-center gap-3 text-2xl sm:text-3xl font-semibold mt-3">
            <Clock className="w-7 h-7 text-cn-primary flex-shrink-0" />
            <span>{friendlyTime(pass?.time)}</span>
          </div>
        </div>

        {/* Arrival + completion tracking (Blueprint #19) */}
        <div className="mt-6 bg-white rounded-3xl border border-cn-border p-6 sm:p-8" data-testid="visit-pass-tracking">
          <p className="text-base text-slate-500 mb-4">Visit progress</p>

          {/* Arrived */}
          {status.arrived_at ? (
            <div className="rounded-2xl bg-cn-sage-50 border border-cn-sage-200 p-4 inline-flex items-center gap-3 text-cn-primary font-semibold" data-testid="visit-pass-arrived">
              <CheckCircle2 className="w-5 h-5" />
              Checked in at {new Date(status.arrived_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
          ) : (
            <button
              onClick={checkIn}
              disabled={checkInBusy || status.completed_at}
              data-testid="visit-pass-checkin"
              className="w-full bg-cn-primary hover:bg-cn-primary/90 text-white text-xl sm:text-2xl font-bold py-5 rounded-2xl inline-flex items-center justify-center gap-3 disabled:opacity-60 transition-colors"
            >
              {checkInBusy ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogIn className="w-6 h-6" />}
              {checkInBusy ? 'Checking in…' : 'Check in (visit starts)'}
            </button>
          )}

          {/* Complete */}
          {status.arrived_at && !status.completed_at && (
            <button
              onClick={completeVisit}
              disabled={completeBusy}
              data-testid="visit-pass-complete"
              className="w-full mt-3 bg-cn-sage-500 hover:bg-cn-sage-600 text-white text-xl sm:text-2xl font-bold py-5 rounded-2xl inline-flex items-center justify-center gap-3 disabled:opacity-60 transition-colors"
            >
              {completeBusy ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogOut className="w-6 h-6" />}
              {completeBusy ? 'Completing…' : 'Complete visit'}
            </button>
          )}

          {status.completed_at && (
            <div className="rounded-2xl bg-cn-primary/10 border border-cn-primary/30 p-4 mt-3 inline-flex items-center gap-3 text-cn-primary font-semibold" data-testid="visit-pass-completed">
              <CheckCircle2 className="w-5 h-5" />
              Visit completed at {new Date(status.completed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
          )}
        </div>

        {/* Primary action */}
        <div className="mt-8">
          {isTele ? (
            <button
              onClick={joinVideo}
              disabled={joining}
              data-testid="visit-pass-join-btn"
              className="w-full bg-cn-primary hover:bg-cn-primary/90 text-white text-2xl sm:text-3xl font-bold py-6 rounded-3xl inline-flex items-center justify-center gap-4 disabled:opacity-60 transition-colors"
            >
              {joining ? <Loader2 className="w-8 h-8 animate-spin" /> : <Video className="w-8 h-8" />}
              {joining ? 'Connecting…' : 'Join my video visit'}
            </button>
          ) : (
            <div className="w-full bg-cn-secondary/10 border border-cn-secondary/30 text-slate-800 text-xl sm:text-2xl font-medium py-6 px-6 rounded-3xl inline-flex items-center justify-center gap-3 text-center" data-testid="visit-pass-inperson">
              <MapPin className="w-7 h-7 text-cn-secondary flex-shrink-0" />
              Your clinician will come to you. Please be ready at this time.
            </div>
          )}
          {isTele && (
            <p className="text-center text-lg text-slate-500 mt-4">
              When you tap the button, you can test your camera and sound first. No app or password needed.
            </p>
          )}
        </div>

        {/* Always-visible help */}
        {pass?.support_phone && (
          <div className="mt-10 text-center" data-testid="visit-pass-help">
            <p className="text-xl text-slate-600">Need help or prefer to talk to a person?</p>
            <a href={`tel:${pass.support_phone}`} className="mt-3 inline-flex items-center gap-3 text-2xl sm:text-3xl font-bold text-cn-primary" data-testid="visit-pass-help-call">
              <Phone className="w-7 h-7" /> {pass.support_phone}
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
